import safeStringify from 'fast-safe-stringify';
import { ErrorConstants } from 'src/constants';

/**
 * Base class for all custom errors
 */
export class BaseError extends Error {
  constructor(message: string, name: string) {
    super(message);
    this.name = name;

    const stackStr = new Error().stack;
    if (stackStr) {
      this.stack = stackStr;
    }
  }
}

/**
 * ValidationError class for throwing specific validation errors
 */
export class ValidationError extends BaseError {
  constructor(message: string) {
    super(message, ErrorConstants.NAMES.VALIDATION);
  }
}

/**
 * InvalidInputException class for throwing errors related to invalid input
 */
export class InvalidInputException extends BaseError {
  constructor(message: string) {
    super(message, ErrorConstants.NAMES.INVALID_INPUT);
  }
}

/**
 * Base class for all polling-related errors
 */
export class PollingError extends BaseError {
  constructor(message: string) {
    super(message, ErrorConstants.NAMES.POLLING_ERROR);
  }
}

/**
 * Error thrown when there is an invalid poller input
 */
export class PollingInputError extends PollingError {
  constructor(message: string) {
    super(message);
    this.name = ErrorConstants.NAMES.POLLING_INPUT_ERROR;
  }
}

/**
 * Error thrown when polling is aborted
 */
export class PollingAbortedError extends PollingError {
  constructor(message = 'Polling operation aborted') {
    super(message);
    this.name = ErrorConstants.NAMES.POLLING_ABORTED_ERROR;
  }
}

/**
 * Error thrown when polling times out
 */
export class PollingTimeoutError extends PollingError {
  constructor(message = 'Operation timed out') {
    super(message);
    this.name = ErrorConstants.NAMES.POLLING_TIMEOUT_ERROR;
  }
}

/**
 * Error thrown when maximum retries are exceeded
 */
export class PollingMaxRetriesError extends PollingError {
  constructor(maxRetries: number) {
    super(`Max retries (${maxRetries}) exceeded.`);
    this.name = ErrorConstants.NAMES.POLLING_MAX_RETRIES_ERROR;
  }
}

/**
 * Error thrown when success condition is not met
 */
export class PollingSuccessConditionError extends PollingError {
  constructor(result: unknown) {
    super(`Operation result did not meet success condition. Result: ${safeStringify(result)}`);
    this.name = ErrorConstants.NAMES.POLLING_SUCCESS_CONDITION_ERROR;
  }
}
